import type {
    ColDef,
    GridOptions,
    ICellRendererParams,
    RowSelectionOptions,
} from '@ag-grid-community/core';
import { StatusPill, StatusPillValue } from '@ics-portal/guardian';
import { formatDate } from '@utils/dateFormatUtils';
import { noGroupDisplayCellRenderer } from './column-utils';
import {
    defaultBooleanFilterParams,
    defaultDateFilterParams,
    defaultSetFilterParams,
    FilterType,
} from './config-filter';
import mapComparator, { RATING_SORT_MAP } from './map-comparator';

const LOCALE_TEXT = {
    rowGroupColumnsEmptyMessage: 'Drag column headers to group on',
};

export const columnTypes = {
    shortDate: {
        filter: FilterType.Date,
        filterParams: defaultDateFilterParams,
        cellRenderer: ({ value }: ICellRendererParams) => {
            if (typeof value === 'string') {
                return formatDate(value);
            }
            return value;
        },
    },
    urlColumn: {
        cellRenderer: ({ value }: ICellRendererParams) => {
            if (typeof value === 'string' && value.startsWith('http')) {
                return <a href={value}>{value}</a>;
            }
            return value;
        },
    },
    rating: {
        filter: FilterType.Set,
        floatingFilter: true,
        filterParams: {
            ...defaultSetFilterParams,
            values: [
                StatusPillValue.HIGH,
                StatusPillValue.MEDIUM,
                StatusPillValue.LOW,
            ],
        },
        cellRendererSelector: noGroupDisplayCellRenderer,
        cellRenderer: (
            params: ICellRendererParams<unknown, StatusPillValue>,
        ) => {
            const rating = params.value;
            if (!rating) {
                return null;
            }
            return <StatusPill value={rating} onlyContent />;
        },
        comparator: mapComparator(RATING_SORT_MAP),
    },
    boolean: {
        filter: FilterType.Text,
        floatingFilter: true,
        filterParams: {
            ...defaultBooleanFilterParams,
        },
        cellDataType: 'text',
        cellRenderer: (
            params: ICellRendererParams<unknown, boolean | string>,
        ) => {
            const value = params.value;
            if (value === true || value === 'true') {
                return 'true';
            } else if (value === false || value === 'false') {
                return 'false';
            }
            return null;
        },
    },
} satisfies GridOptions['columnTypes'];

export const defaultConfiguration = {
    defaultColDef: {
        // allow every column to be pivoted
        enablePivot: false,
        floatingFilter: false,
        filter: false,
        sortable: true,
        resizable: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        enableRowGroup: false,
        unSortIcon: true,
        minWidth: 180,
        filterParams: {
            debounceMs: 300,
        },
        flex: 1,
    } as Omit<ColDef, 'field'>,

    rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
    },

    defaultColGroupDef: {
        autoHeaderHeight: false,
        marryChildren: true,
        openByDefault: true,
        columnGroupShow: 'open',
    },
    statusBar: undefined,
    detailRowAutoHeight: true,
    animateRows: true,
    groupHeaderHeight: 24,
    rowGroupPanelShow: 'never',
    domLayout: 'autoHeight',
    pagination: true,
    paginationPageSizeSelector: false,
    suppressStickyTotalRow: true,

    localeText: LOCALE_TEXT,
} satisfies GridOptions;

export const masterDetails = {
    masterDetail: true,
} satisfies GridOptions;

export const multiSelect: { rowSelection: RowSelectionOptions } = {
    rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true,
    },
} satisfies GridOptions;

export const singleSelect: { rowSelection: RowSelectionOptions } = {
    rowSelection: {
        mode: 'singleRow',
        checkboxes: true,
        enableClickSelection: true,
    },
} satisfies GridOptions;

export const rowGroup = {
    rowGroupPanelShow: 'never',
} satisfies GridOptions;
